// The workspaces live on the machine (spec "Programmable terminal", docs/protocol.md "Workspaces on the machine"): this
// browser changes its own copy at once with tabs.ts, then sends the machine the groups it changed (layout.put); the
// machine's whole state comes back on every change (the layout event) and takes the place of the groups here, keeping
// what is this browser's own: the row shown, the page over the panes, blank tabs (+) and panes still empty after a
// split. Pure, so it's tested without a browser; useWorkspaceSync sends and hears.

import type { LayoutGroup, MachineLayout } from '../net/protocol';
import { current, dropPane, insertLeaf, isBlank, MAX_PANES, members, paneOrder, settle, withoutTab, type Group, type Layout, type Pane, type TabsState } from './tabs';

export type LayoutPut = { groups: LayoutGroup[]; drop: { id: number; ver: number }[]; seed?: boolean };

/** The machine's state as JSON has it, whole: Go sends no groups (none yet) as null. */
export const machineLayout = (l: MachineLayout): MachineLayout => ({ ...l, groups: (l.groups ?? []).map((g) => ({ ...g, panes: (g.panes ?? []).map((p) => ({ ...p, tabs: p.tabs ?? [] })) })) });

const withoutBlanks = (p: Pane): Pane => p.tabs.reduceRight((q, id, k) => (isBlank(id) ? withoutTab(q, k) : q), p);

/** Group `g` as the machine keeps it: no blank tabs, no pane without a session (null: nothing left of it). */
export function onMachine(g: Group): Group | null {
  let out: Group | null = { ...g, panes: g.panes.map(withoutBlanks) };
  for (let i = out.panes.length - 1; i >= 0 && out; i--) if (!out.panes[i].tabs.length) out = dropPane(out, i);
  return out && settle(out);
}

const tree = (l: Layout): unknown => ('pane' in l ? l.pane : [l.dir, l.sizes, l.kids.map(tree)]);

/** What a group holds and how, the same here and on the machine: its lead, focus, panes and tree (not its id or ver). */
export const shape = (g: Group) => JSON.stringify([g.lead, g.focused, g.panes.map((p) => [p.tabs, p.active]), tree(g.layout)]);

/** A session alone: one pane, one tab. */
const lone = (g: Group) => g.panes.length === 1 && g.panes[0].tabs.length === 1;

const wire = (g: Group, ver: number): LayoutGroup => ({ id: g.id, lead: g.lead, panes: g.panes.map(({ tabs, active }) => ({ tabs, active })), focused: g.focused, layout: g.layout, ver });

/**
 * What to send for the changes here since `heard`: each group whose machine form isn't the machine's (with the ver
 * heard; 0 for one made here) and the ones gone here. A group made here holding one session alone isn't sent: the
 * machine has a row for every session already. Null: nothing changed.
 */
export function changes(local: TabsState, heard: MachineLayout): LayoutPut | null {
  const had = new Map(heard.groups.map((g) => [g.id, g]));
  const here = new Set<number>();
  const groups: LayoutGroup[] = [];
  for (const g of local.groups) {
    const m = onMachine(g);
    if (!m) continue;
    here.add(g.id);
    const h = had.get(g.id);
    if (h ? shape(m) !== shape(h) : !lone(m)) groups.push(wire(m, h?.ver ?? 0));
  }
  const drop = heard.groups.filter((h) => !here.has(h.id)).map((h) => ({ id: h.id, ver: h.ver }));
  return groups.length || drop.length ? { groups, drop } : null;
}

/** This browser's own workspaces from before the machine kept them, for a machine that has none (`seed`): its groups
 *  of two or more sessions (the machine gives them ids of its own). Null: nothing to give. */
export function seedPut(legacy: TabsState): LayoutPut | null {
  const groups = legacy.groups.flatMap((g) => {
    const m = onMachine(g);
    return m && members(m).length > 1 ? [wire(m, 0)] : [];
  });
  return groups.length ? { groups, drop: [], seed: true } : null;
}

/** The first state a page hears: the machine's groups, and the row it showed found again, by its group's id (when the
 *  ids it kept are the machine's) or else by its lead. */
export function adopt(next: MachineLayout, page: TabsState['page'], at: number | null, lead: number | null): TabsState {
  const groups: Group[] = next.groups.map((g) => ({ ...g }));
  const shown = groups.find((g) => g.id === at) ?? groups.find((g) => g.lead === lead);
  return { groups, at: shown?.id ?? null, page };
}

/** Group `g` without session `id`: a pane left with no tab closes, a group with none left goes. */
function without(g: Group, id: number): Group | null {
  const i = g.panes.findIndex((p) => p.tabs.includes(id));
  if (i < 0) return g;
  const pane = withoutTab(g.panes[i], g.panes[i].tabs.indexOf(id));
  const out = pane.tabs.length ? { ...g, panes: g.panes.map((p, k) => (k === i ? pane : p)) } : dropPane(g, i);
  return out && settle(out);
}

/** Group `l` as it is here, with what the machine says of it alone (its ver, tmux's session, index and name): `l`
 *  itself when that's the same, so nothing redraws. */
function mixed(n: LayoutGroup, l: Group): Group {
  const was = l as Partial<LayoutGroup>;
  const same = was.ver === n.ver && was.sess === n.sess && was.index === n.index && was.name === n.name && was.zoomed === n.zoomed && was.made === n.made;
  return same ? l : { ...n, lead: l.lead, panes: l.panes, focused: l.focused, layout: l.layout };
}

/** The session pane `i` shows, blank tabs counted. */
const showing = (g: Group, i: number) => g.panes[i]?.tabs[g.panes[i].active];

/** The split pane `i`'s leaf is in: its direction, its children and the leaf's place among them. */
function parentOf(l: Layout, i: number): { dir: 'row' | 'col'; kids: Layout[]; k: number } | null {
  if ('pane' in l) return null;
  const k = l.kids.findIndex((c) => 'pane' in c && c.pane === i);
  if (k >= 0) return { dir: l.dir, kids: l.kids, k };
  for (const c of l.kids) {
    const p = parentOf(c, i);
    if (p) return p;
  }
  return null;
}

/**
 * The machine's group `n` with what this browser had of its own in it (`l`, as it was here; `w`, as the machine had it
 * before): blank tabs go back among the tabs of the pane that still holds the same sessions, a pane with no session
 * yet goes back beside the pane it was split from (not past 4), and one showing a blank tab keeps it shown, focused
 * when it was and the machine didn't move the focus.
 */
function withOwn(n: LayoutGroup, l: Group | undefined, w: LayoutGroup | undefined): Group {
  let out: Group = { ...n };
  if (!l) return out;
  const focusMoved = !w || showing(n, n.focused) !== showing(w, w.focused);
  l.panes.forEach((p, i) => {
    const sessions = p.tabs.filter((id) => !isBlank(id));
    if (sessions.length === p.tabs.length && p.tabs.length) return;
    const blankShown = !p.tabs.length || isBlank(p.tabs[p.active]);
    const keepsFocus = blankShown && l.focused === i && !focusMoved;
    const j = sessions.length ? out.panes.findIndex((q) => q.tabs.some((id) => sessions.includes(id))) : -1;
    if (j >= 0) {
      const q = out.panes[j];
      const tabs = [...q.tabs];
      p.tabs.forEach((id, k) => isBlank(id) && tabs.splice(Math.min(k, tabs.length), 0, id));
      const active = tabs.indexOf(blankShown ? p.tabs[p.active] : q.tabs[q.active]);
      out = { ...out, panes: out.panes.map((x, m) => (m === j ? { tabs, active } : x)), focused: keepsFocus ? j : out.focused };
      return;
    }
    if (sessions.length || out.panes.length >= MAX_PANES) return; // its sessions went elsewhere, and so does it
    const at = parentOf(l.layout, i);
    const beside = at && (at.kids[at.k - 1] ?? at.kids[at.k + 1]);
    const near = beside ? paneOrder(beside).map((x) => l.panes[x]?.tabs.find((id) => !isBlank(id))).find((id) => id !== undefined) : undefined;
    const to = near === undefined ? -1 : out.panes.findIndex((q) => q.tabs.includes(near));
    if (!at || to < 0) return;
    const ni = out.panes.length;
    out = { ...out, panes: [...out.panes, { tabs: p.tabs, active: p.active }], layout: insertLeaf(out.layout, to, ni, at.dir, !at.kids[at.k - 1]), focused: keepsFocus ? ni : out.focused };
  });
  return out;
}

/**
 * The workspace here once the machine's state `next` is heard (an event, or a put's answer), `was` being the one heard
 * before: the machine's groups, except one changed here that the machine hasn't taken yet (its put is on the way, or
 * still to go) and didn't change itself since: that one stays as it is here, to be sent. One the machine changed since
 * is the machine's (the put would be stale). `sent`: the shapes the put on the way sent, by group id, so the machine
 * taking one reads as heard back; `answered`: `next` is that put's answer, so a group it made that isn't there is gone.
 * Every session ends up in one group, the ones kept here first (the machine will move them so). What's this browser's
 * stays: the row shown (by id, else the group with the same lead, else none), the page, blank tabs and empty panes.
 */
export function merge(local: TabsState, was: MachineLayout | null, next: MachineLayout, sent: ReadonlyMap<number, string> = new Map(), answered = false): TabsState {
  const before = new Map(was?.groups.map((g) => [g.id, g]));
  const mine = new Map(local.groups.map((g) => [g.id, g]));
  const inNext = new Set(next.groups.map((g) => g.id));
  const ours: Group[] = []; // as here, still to be sent
  const theirs: Group[] = []; // as the agent keeps it (with this browser's own bits)
  const loose: Group[] = []; // one session alone, made here and never sent: the agent has it somewhere already
  const own: Group[] = []; // nothing but blank tabs: only this browser's
  for (const n of next.groups) {
    const l = mine.get(n.id);
    const w = before.get(n.id);
    if (!l) {
      if (!w || w.ver !== n.ver) theirs.push({ ...n }); // else it went here, and goes there with the next put
      continue;
    }
    const m = onMachine(l);
    if (m && shape(m) === shape(n)) theirs.push(mixed(n, l));
    else if ((!m || !w || shape(m) !== shape(w)) && ((w && w.ver === n.ver) || sent.get(n.id) === shape(n))) ours.push(mixed(n, l));
    else theirs.push(withOwn(n, l, w));
  }
  for (const l of local.groups) {
    if (inNext.has(l.id)) continue;
    const m = onMachine(l);
    if (!m) own.push(l);
    else if (before.has(l.id) || (answered && sent.has(l.id))) continue; // the agent let it go, or didn't take it
    else if (lone(m) && !sent.has(l.id)) loose.push(l);
    else ours.push(l);
  }
  const claimed = new Set<number>();
  const groups: Group[] = [];
  for (const g of [...ours, ...theirs, ...loose]) {
    const out = members(g).reduce<Group | null>((x, id) => (x && claimed.has(id) ? without(x, id) : x), g);
    if (!out) continue;
    members(out).forEach((id) => claimed.add(id));
    groups.push(out);
  }
  groups.push(...own);
  const lead = current(local)?.lead;
  const at = groups.find((g) => g.id === local.at) ?? groups.find((g) => g.lead === lead);
  return { groups, at: at?.id ?? null, page: local.page };
}
